import { type EngineEventMap } from '../core/EngineEvents';
import { type EventEmitter } from '../events/EventEmitter';

/** How one call to {@link PointerLock.request} differs from the defaults. */
export interface PointerLockRequest {
  /**
   * Whether to ask for movement with the operating system's acceleration curve stripped out.
   *
   * Off by default, because what a lock usually drives here is a cursor, and every cursor the player
   * has ever used has that curve on it: small movements precise, fast flicks crossing the screen.
   * Removing it does not make the cursor feel slower, it makes it feel unfamiliar. Turn it on for
   * aiming, where a fixed relationship to physical distance matters more. Browsers that cannot do it
   * fall back to the curve rather than failing the request.
   */
  readonly rawMovement?: boolean;
}

/** Construction options for {@link PointerLock}. */
export interface PointerLockOptions {
  /** Element the lock is taken on, normally the canvas. Omit to leave locking unavailable. */
  readonly element?: HTMLElement | undefined;
  /** Bus `pointerLockChanged` is published on. */
  readonly events: EventEmitter<EngineEventMap>;
}

/** Media query that answers whether there is a pointing device precise enough to be worth locking. */
const FINE_POINTER_QUERY = '(pointer: fine)';

/**
 * Takes and releases the browser's pointer lock, and is the one place that knows whether it is held.
 *
 * Two things need that answer and they are far apart: a game, which decides whether to ask for a lock
 * at all and what to do when it goes away, and `DomInputBinding`, which has to know whether the browser
 * is currently reporting positions or movement. Both read it here rather than each watching the
 * document, so they cannot disagree.
 *
 * Locking is never automatic. Audio can unlock itself on the first tap because doing so is invisible
 * and always wanted; seizing the mouse is a mode change the player has to mean, so it takes an explicit
 * call from a real interaction.
 *
 * A context built without an element, as every test harness is, gets an instance that supports nothing
 * and locks nothing.
 */
export class PointerLock {
  readonly #element: HTMLElement | undefined;
  readonly #events: EventEmitter<EngineEventMap>;
  readonly #document: Document | undefined;

  #locked = false;
  #settlePending: ((isLocked: boolean) => void) | undefined;
  #disposed = false;

  constructor(options: PointerLockOptions) {
    this.#element = options.element;
    this.#events = options.events;
    this.#document = options.element?.ownerDocument;

    this.#document?.addEventListener('pointerlockchange', this.#onLockChange);
    this.#document?.addEventListener('pointerlockerror', this.#onLockError);
  }

  /** Whether the pointer is locked to the canvas right now. */
  get isLocked(): boolean {
    return this.#locked;
  }

  /**
   * Whether asking for a lock could succeed on this device.
   *
   * Deliberately not a check for the API alone, which exists on phones and tablets that have no mouse
   * to lock. It is paired with the same "is there a precise pointer" question CSS asks before applying
   * a hover style, so a tablet correctly reports false and a laptop with a trackpad correctly reports
   * true. A tablet with a mouse paired to it is a grey area, and it errs towards saying no.
   *
   * A hint for deciding what kind of scene to build, not a promise: {@link PointerLock.request} is
   * still the only thing that knows whether a lock was actually granted.
   */
  get isSupported(): boolean {
    if (this.#element === undefined || typeof this.#element.requestPointerLock !== 'function') {
      return false;
    }
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return false;
    }
    return window.matchMedia(FINE_POINTER_QUERY).matches;
  }

  /**
   * Asks the browser for the lock and resolves with whether it was granted.
   *
   * A request can be turned down for reasons a caller cannot see: no recent interaction to justify it,
   * a browser that throttles a second attempt in the seconds after the player escaped out of the last
   * one, or simply no mouse. Nothing here retries; a caller that gets false should fall back rather
   * than ask again, because a fast second attempt is exactly what browsers throttle.
   *
   * The answer comes from the document's own lock events rather than from what the request returned,
   * because older browsers return nothing at all and report through those events alone.
   */
  async request(options: PointerLockRequest = {}): Promise<boolean> {
    const element = this.#element;
    if (element === undefined || this.#disposed || !this.isSupported) {
      return false;
    }
    if (this.#locked) {
      return true;
    }

    const granted = await this.#attempt(element, options.rawMovement ?? false);
    if (granted || options.rawMovement !== true) {
      return granted;
    }
    return this.#attempt(element, false);
  }

  /** Releases the lock if this canvas holds it. Does nothing otherwise. */
  exit(): void {
    if (this.#locked) {
      this.#document?.exitPointerLock();
    }
  }

  /** Stops listening to the document and abandons any request still waiting for an answer. */
  dispose(): void {
    if (this.#disposed) {
      return;
    }
    this.#disposed = true;

    this.#document?.removeEventListener('pointerlockchange', this.#onLockChange);
    this.#document?.removeEventListener('pointerlockerror', this.#onLockError);
    this.#settle(false);
  }

  /*
   * The returned value is only awaited to keep a rejection from going unhandled, never to decide the
   * outcome. A browser that rejects the promise also fires the error event, and a browser too old to
   * return a promise fires nothing else, so the events are the one source both cases share.
   */
  async #attempt(element: HTMLElement, rawMovement: boolean): Promise<boolean> {
    const outcome = new Promise<boolean>((resolve) => {
      this.#settlePending = resolve;
    });

    const returned: unknown = element.requestPointerLock({ unadjustedMovement: rawMovement });
    if (returned instanceof Promise) {
      returned.catch(() => {
        this.#settle(false);
      });
    }

    return outcome;
  }

  #settle(isLocked: boolean): void {
    const pending = this.#settlePending;
    this.#settlePending = undefined;
    pending?.(isLocked);
  }

  readonly #onLockChange = (): void => {
    const isLocked = this.#document?.pointerLockElement === this.#element;
    this.#settle(isLocked);

    if (isLocked === this.#locked) {
      return;
    }
    this.#locked = isLocked;
    this.#events.emit('pointerLockChanged', { isLocked });
  };

  readonly #onLockError = (): void => {
    this.#settle(false);
  };
}
